'use client';

import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useState } from 'react';
import { Mail, Send, MessageSquare, Building, User } from 'lucide-react';

interface ContactFormData {
  name: string;
  email: string;
  business: string;
  service: string;
  message: string;
  budget: string;
}

const SERVICE_OPTIONS = [
  'Website Design & Build',
  'Marketing & SEO',
  'Email Outreach Campaigns',
  'Social Media Management',
  'Automation & Workflow Setup',
  'Not sure yet — let\'s talk',
];

const BUDGET_OPTIONS = [
  'Under $1,000',
  '$1,000 – $5,000',
  '$5,000 – $15,000',
  '$15,000+',
  'Ongoing retainer',
];

export default function ContactSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>();

  // Backend integration point: replace with API call to send email or store in CRM
  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result?.error || 'Failed to send message');
      }

      setSubmitted(true);
      reset();
      toast.success("Message sent! I'll get back to you within 24 hours.");
    } catch (err: any) {
      toast.error(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-slate-50">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-12 xl:gap-20 items-start">
          {/* Left: info */}
          <div>
            <p className="text-sm font-semibold text-green-600 uppercase tracking-widest mb-2">
              Let's Work Together
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-5">
              Tell me about your project
            </h2>
            <p className="text-slate-500 leading-relaxed mb-8">
              Fill out the form and I'll respond within 24 hours. Discovery calls are free —
              no commitment, no sales pitch. Just a conversation about what you need and
              whether I'm the right fit.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200">
                <div className="p-2.5 bg-green-100 rounded-lg">
                  <Mail size={18} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Direct Email</p>
                  <a
                    href="mailto:mactivated90@gmail.com"
                    className="text-sm text-green-600 hover:text-green-700 transition-colors"
                  >
                    mactivated90@gmail.com
                  </a>
                  <p className="text-xs text-slate-400 mt-1">For urgent inquiries</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200">
                <div className="p-2.5 bg-blue-100 rounded-lg">
                  <MessageSquare size={18} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Response Time</p>
                  <p className="text-sm text-slate-600">Within 24 hours, usually same day</p>
                  <p className="text-xs text-slate-400 mt-1">Mon–Fri, 9am–6pm EST</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            {submitted ? (
              <div className="text-center py-8 fade-in">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send size={24} className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Message received!</h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Thanks for reaching out. I'll review your project details and get back to
                  you within 24 hours with next steps.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-sm font-medium text-green-600 hover:text-green-700 underline underline-offset-2"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-1.5">
                      <User size={12} className="inline mr-1" />
                      Your Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      placeholder="Jane Smith"
                      suppressHydrationWarning
                      className={`w-full border rounded-lg px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 outline-none transition-all duration-150 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 ${
                        errors.name ? 'border-red-400' : 'border-slate-200'
                      }`}
                      {...register('name', { required: 'Name is required' })}
                    />
                    {errors.name && (
                      <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-semibold text-slate-700 mb-1.5">
                      <Mail size={12} className="inline mr-1" />
                      Email Address <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      placeholder="jane@company.com"
                      suppressHydrationWarning
                      className={`w-full border rounded-lg px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 outline-none transition-all duration-150 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 ${
                        errors.email ? 'border-red-400' : 'border-slate-200'
                      }`}
                      {...register('email', {
                        required: 'Email is required',
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                      })}
                    />
                    {errors.email && (
                      <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                {/* Business */}
                <div>
                  <label htmlFor="business" className="block text-sm font-semibold text-slate-700 mb-1.5">
                    <Building size={12} className="inline mr-1" />
                    Business Name
                  </label>
                  <input
                    id="business"
                    type="text"
                    placeholder="Smith & Co."
                    suppressHydrationWarning
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 outline-none transition-all duration-150 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                    {...register('business')}
                  />
                </div>

                {/* Service */}
                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Service Needed <span className="text-red-400">*</span>
                  </label>
                  <select
                    id="service"
                    suppressHydrationWarning
                    className={`w-full border rounded-lg px-4 py-2.5 text-sm text-slate-800 outline-none transition-all duration-150 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 bg-white ${
                      errors.service ? 'border-red-400' : 'border-slate-200'
                    }`}
                    {...register('service', { required: 'Please select a service' })}
                  >
                    <option value="">Select a service...</option>
                    {SERVICE_OPTIONS.map((opt) => (
                      <option key={`svc-opt-${opt}`} value={opt}>{opt}</option>
                    ))}
                  </select>
                  {errors.service && (
                    <p className="text-xs text-red-500 mt-1">{errors.service.message}</p>
                  )}
                </div>

                {/* Budget */}
                <div>
                  <label htmlFor="budget" className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Approximate Budget
                  </label>
                  <select
                    id="budget"
                    suppressHydrationWarning
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-800 outline-none transition-all duration-150 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 bg-white"
                    {...register('budget')}
                  >
                    <option value="">Prefer not to say</option>
                    {BUDGET_OPTIONS.map((opt) => (
                      <option key={`budget-opt-${opt}`} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Tell me about your project <span className="text-red-400">*</span>
                  </label>
                  <p className="text-xs text-slate-400 mb-2">
                    What are you trying to achieve? What's not working right now?
                  </p>
                  <textarea
                    id="message"
                    rows={4}
                    placeholder="We're a local plumbing company with no website. Customers can't find us online and we're losing business to competitors who show up on Google..."
                    className={`w-full border rounded-lg px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 outline-none transition-all duration-150 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 resize-none ${
                      errors.message ? 'border-red-400' : 'border-slate-200'
                    }`}
                    {...register('message', { required: 'Tell me a bit about your project' })}
                  />
                  {errors.message && (
                    <p className="text-xs text-red-500 mt-1">{errors.message.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-slate-900 hover:bg-slate-700 text-white font-semibold text-sm py-3.5 rounded-xl transition-all duration-150 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
                  style={{ minHeight: '50px' }}
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={15} />
                      Send Message
                    </>
                  )}
                </button>

                <p className="text-xs text-slate-400 text-center">
                  No spam, no cold calls. Just a conversation.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}